/**
 * How much of what a class will actually trade the answer key marks.
 *
 * WHY THIS IS A SEPARATE FACT FROM THE VALIDITY VERDICT. The verdict
 * (`readAnswerKeyGate`) answers "can this answer key tell skill from
 * clicking". Coverage answers a different question, and a key can pass one
 * while failing the other: THE WAIT v6 grades ten of the thirteen instruments
 * its class can trade, so a trainee who expresses their whole view through
 * one of the other three is marked as having said nothing. The verdict has no
 * opinion about that, and until this module existed neither did any screen.
 *
 * WHAT AN INJECTED PAIR IS. A session's tradable set is not the template's
 * asset list. PM adds an FX pair at session start for every currency the
 * template holds an instrument in but cannot pay for — a gilt in a
 * USD-reporting scenario brings GBP= with it — so the trainee can buy the
 * currency. Those pairs are tradable, they are markable, and they are the
 * ones most likely to be missing from a key that was derived off the template
 * alone. They are labelled wherever they appear, because "why is GBP= on this
 * list at all" is the first question an admin asks.
 *
 * ABSENT IS NOT EMPTY, exactly as in `readAnswerKeyGate`. The blueprint
 * broadcast reaches every open dashboard at once and cannot check who is
 * listening, so it carries no admin-only fields; a card that arrived that way
 * has NO coverage key, and reading that as "nothing is graded" would put a
 * false warning in front of a trainer. No key at all means this delivery did
 * not carry it (`undefined`); a key present and empty means there genuinely
 * are no counts on record (`null`).
 */

/** The counts as the launch card serves them (`_build_launch_summaries`). */
export type AnswerKeyCoverageCounts = {
  tradable?: number | null;
  graded?: number | null;
  not_graded?: number | null;
  injected_fx?: number | null;
  not_graded_tickers?: string[] | null;
  injected_fx_tickers?: string[] | null;
  complete?: boolean | null;
};

/** The one shape every screen reads. */
export type AnswerKeyCoverage = {
  /** Instruments the class can trade in a STARTED session, injected included. */
  tradable: number;
  graded: number;
  notGraded: number;
  /** How many of the tradable instruments PM adds at session start. */
  injectedFx: number;
  notGradedTickers: string[];
  injectedFxTickers: string[];
  /** Every tradable instrument is graded. */
  complete: boolean;
};

/**
 * D10, DEFERRED AND RECORDED HERE SO IT IS NOT LOST.
 *
 * There are two places where a person can change what a session trades or
 * shows AFTER an answer key has been derived for it, and the key cannot mark
 * the change:
 *
 *   1. `src/views/TrainerSession/SessionSettingsDense/instruments_v2`
 *      (InstrumentsPanelV2), on "Save current instrument selection" and on
 *      "Save player visibility", for a session launched from a blueprint that
 *      has a key; and
 *   2. the admin News tab row, when the headline being edited is one the
 *      served key scored.
 *
 * The copy, in both places: "The answer key cannot mark this change".
 *
 * It is not built in this phase because neither surface knows which key is
 * attached: the session payload does not carry the attached key's code. That
 * is the prerequisite, and it is a backend change, not a frontend one.
 */

/** The chip on a row PM added, and the sentence explaining why it is there. */
export const ADDED_BY_PM_CHIP = 'Added by PM';
export const ADDED_BY_PM_TOOLTIP =
  'PM adds this so trainees can exchange currency';

/**
 * The chip a variant with NO answer key wears, in place of a fraction.
 *
 * "0 / 2 graded" and "2 / 2 graded" are both answers to a question nobody can
 * ask yet: there is no marking scheme, so no instrument on this exercise is
 * graded or ungraded, and a fraction invites an admin to read a number as
 * progress. This is the whole fact, and it is the same fact the launch modal
 * gives a trainer ("Answer key: None -- this session is not graded").
 */
export const NO_ANSWER_KEY_CHIP = 'No answer key';

/** The warning a trainer sees before launching an incompletely covered key. */
export const INCOMPLETE_COVERAGE_HEADLINE =
  'This answer key does not cover everything the class will trade';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Whether a launch card names an answer key AT ALL -- one that attaches, or
 * one the endpoint is refusing to attach (`answer_key_status` carries the
 * refusal's own code).
 *
 * ABSENT IS NOT EMPTY. A delivery that carried no card (the socket broadcast)
 * knows nothing about this variant, and answering `false` for it would put
 * "No answer key" in front of an admin on the strength of a field nobody
 * sent, so a caller asks `hasCard` first.
 */
export const hasAnswerKey = (source: unknown): boolean => {
  if (!isObject(source)) return false;
  return Boolean(source.answer_key || source.answer_key_status);
};

/** Whether this delivery carried the launch card's coverage at all. */
export const hasCard = (source: unknown): boolean =>
  isObject(source) && isObject(source.coverage);

const count = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const tickers = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(row => String(row ?? '').trim()).filter(Boolean)
    : [];

/** The per-asset rows of the admin document, when the caller has them. */
const tickersFromRows = (
  rows: unknown,
): { notGraded: string[]; injected: string[] } => {
  const result = { notGraded: [] as string[], injected: [] as string[] };
  if (!Array.isArray(rows)) return result;
  rows.filter(isObject).forEach(row => {
    const ticker = String(row.ticker ?? row.name ?? '').trim();
    if (!ticker) return;
    if (String(row.status || '') !== 'graded') result.notGraded.push(ticker);
    if (row.injected) result.injected.push(ticker);
  });
  return result;
};

const fold = (
  counts: AnswerKeyCoverageCounts,
  rows?: unknown,
): AnswerKeyCoverage => {
  const fromRows = tickersFromRows(rows);
  const tradable = count(counts.tradable);
  const graded = count(counts.graded);
  const named = tickers(counts.not_graded_tickers);
  const injectedNamed = tickers(counts.injected_fx_tickers);
  return {
    tradable,
    graded,
    notGraded: count(counts.not_graded),
    injectedFx: count(counts.injected_fx),
    notGradedTickers: named.length ? named : fromRows.notGraded,
    injectedFxTickers: injectedNamed.length ? injectedNamed : fromRows.injected,
    // The card answers this itself; a document is complete when its own
    // counts say every tradable instrument is graded.
    complete:
      counts.complete != null ? Boolean(counts.complete) : graded === tradable,
  };
};

/**
 * Normalise whatever the caller has — a launch card, the admin coverage
 * document, or the counts themselves — into the one shape the sentences read.
 *
 * `undefined` means render nothing: this delivery did not carry the fact.
 * `null` means the counts are genuinely not on record.
 */
export const readCoverage = (
  source: unknown,
): AnswerKeyCoverage | null | undefined => {
  if (!isObject(source)) return undefined;
  if ('coverage' in source) {
    const value = source.coverage;
    if (value === undefined) return undefined;
    if (!isObject(value) || Object.keys(value).length === 0) return null;
    return fold(value as AnswerKeyCoverageCounts, source.rows);
  }
  if ('counts' in source) {
    const value = source.counts;
    if (value === undefined) return undefined;
    if (!isObject(value) || Object.keys(value).length === 0) return null;
    return fold(value as AnswerKeyCoverageCounts, source.rows);
  }
  return undefined;
};

/**
 * '10 of 13 tradable instruments' -- the counts with no verb, for a row that
 * is already labelled "Graded". The sentence below is built from it, so the
 * card and the panel cannot end up counting differently.
 */
export const gradedFraction = (
  coverage: AnswerKeyCoverage | null | undefined,
): string => {
  if (!coverage) return '';
  const { tradable, graded } = coverage;
  return `${graded} of ${tradable} tradable instrument${
    tradable === 1 ? '' : 's'
  }`;
};

/** '10 of 13 tradable instruments are graded'. */
export const coverageSentence = (
  coverage: AnswerKeyCoverage | null | undefined,
): string => {
  if (!coverage) return '';
  const { tradable, graded } = coverage;
  if (tradable === 0) return 'This scenario has no tradable instruments';
  if (tradable === 1) {
    return graded === 1
      ? 'The one tradable instrument is graded'
      : 'The one tradable instrument is not graded';
  }
  if (graded === tradable) {
    return `All ${tradable} tradable instruments are graded`;
  }
  return `${gradedFraction(coverage)} ${graded === 1 ? 'is' : 'are'} graded`;
};

/**
 * 'Not graded: GBP= (added by PM for currency exchange), ATM PUT SP500 VALUE'.
 *
 * A count with no names is still worth saying: it is the difference between
 * "three of these are not graded" and a screen that mentions nothing.
 */
export const notGradedLine = (
  coverage: AnswerKeyCoverage | null | undefined,
): string => {
  if (!coverage || coverage.notGraded === 0) return '';
  const injected = new Set(coverage.injectedFxTickers);
  const names = coverage.notGradedTickers;
  if (!names.length) {
    return `${coverage.notGraded} instrument${
      coverage.notGraded === 1 ? ' is' : 's are'
    } not graded`;
  }
  return `Not graded: ${names
    .map(name =>
      injected.has(name) ? `${name} (added by PM for currency exchange)` : name,
    )
    .join(', ')}`;
};

/** '13 tradable · 10 graded · 3 not graded · 1 added by PM'. */
export const countsLine = (
  coverage: AnswerKeyCoverage | null | undefined,
): string => {
  if (!coverage) return '';
  const parts = [
    `${coverage.tradable} tradable`,
    `${coverage.graded} graded`,
    `${coverage.notGraded} not graded`,
  ];
  if (coverage.injectedFx > 0) {
    parts.push(`${coverage.injectedFx} added by PM`);
  }
  return parts.join(' · ');
};
